/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * TypeScript/React version of TaskDocLink
 * Based on the original JSX version in /lego/page/tasks/TaskDocLink.jsx
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { WorkflowType, WorkflowStatus, TASK_ACTIONS } from '../../constants/workflow'
import { FsiType } from '../../constants/fsi'
import { FileAclLevel } from '../../types/acl'
import DocIcon from './DocIcon'
import DocState from './DocState'
import TextLinkButton from './TextLinkButton'
import { Link } from '../common/Link'
import Img from '../Img'
import useCheckDocAccessInQC from '../../hooks/useCheckDocAccessInQC'
import { useModal } from '../../contexts/ModalContext'
import { useServiceContext } from '../../root'
import { getDetails } from '../../services/api/files'
import ParticipatingFsisModal from '../modals/ParticipatingFsisModal'

interface FileSystemItem {
  id: string | number
  name?: string
  type?: string
  valid?: boolean
  systemFolderType?: string
  acl?: any
  aclLevel?: string // ACL level from API (e.g., 'NONE', 'BROWSE', 'VIEW', 'READ', 'WRITE', 'DELETE')
  [key: string]: any
}

interface WorkflowFile {
  draftFsi?: FileSystemItem
  targetFsi?: FileSystemItem
  sourceFsi?: FileSystemItem
  [key: string]: any
}

interface WorkflowInfo {
  id: number
  formattedId?: string
}

interface Task {
  id: number
  name: string
  status?: string
  taskType?: string
  workflowType?: string
  workflowStepType?: string
  workflowDescription?: string
  workflowId?: number
  workflow?: WorkflowInfo
  files?: WorkflowFile[]
  filesCount?: number
  document?: {
    name: string
  }
  [key: string]: any
}

interface TaskDocLinkProps {
  task: Task
  isWorkflow?: boolean
  hideIcon?: boolean // Hide document icon (for modals)
  noPadding?: boolean // Remove padding from multiple docs link
  keepParentModalsOpen?: boolean // Keep parent modals open when opening participating FSIs modal
  onNavigate?: () => void // Callback before navigation (to close parent modals)
}

/**
 * Utility function to get the working file from a workflow/task
 * Based on the original WfService.getWorkingFileFromWF logic
 */
const getWorkingFileFromTask = (task: Task): FileSystemItem | null => {
  if (!task || !task.files || !Array.isArray(task.files) || task.files.length === 0) {
    return null
  }

  if (task.files[0].draftFsi) {
    return task.files[0].draftFsi
  } else if (task.files[0].targetFsi) {
    return task.files[0].targetFsi
  } else if (task.files[0].sourceFsi) {
    return task.files[0].sourceFsi
  }

  return null
}

/**
 * Utility function to truncate text to a specified length
 * Based on the original excerpt filter
 */
const excerpt = (text: string, useHtml: boolean = false, length: number = 40): string => {
  if (!text) return ''

  if (text.length <= length) {
    return text
  }

  return text.substring(0, length) + '...'
}

const TaskDocLink: React.FC<TaskDocLinkProps> = ({
  task,
  isWorkflow = false,
  hideIcon = false,
  noPadding = false,
  keepParentModalsOpen = false,
  onNavigate
}) => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const [workingFile, setWorkingFile] = useState<FileSystemItem | null>(null)
  const [isActiveWf, setIsActiveWf] = useState<boolean>(true)
  const { checkAccess } = useCheckDocAccessInQC(task as any, isWorkflow)
  const { showModal } = useModal()
  const { service } = useServiceContext()

  useEffect(() => {
    const file = getWorkingFileFromTask(task)
    setWorkingFile(file)
    setIsActiveWf(isWorkflow ? WorkflowStatus.ACTIVE === task.status : true)
  }, [task, isWorkflow])

  const handleMultipleDocsClick = () => {
    if (task?.workflowType === WorkflowType.QC_REVIEW) {
      // For QC Review workflows, close parent modal if callback provided
      if (onNavigate) {
        onNavigate()
      }
      // Then check access and handle redirection
      checkAccess()
    } else {
      // Show participating FSIs modal
      const wfId = isWorkflow ? task.id : task.workflow?.id
      if (wfId) {
        showModal(<ParticipatingFsisModal wfId={wfId} show={true} onClose={() => {}} />, {
          keepParentModalsOpen: keepParentModalsOpen
        })
      }
    }
  }

  const handleDocumentClick = async (event?: React.MouseEvent) => {
    // Prevent default link behavior
    if (event) {
      event.preventDefault()
    }

    if (task?.workflowType === WorkflowType.QC_REVIEW) {
      // For QC Review workflows, close parent modal if callback provided
      if (onNavigate) {
        onNavigate()
      }
      // Then check access and handle redirection
      checkAccess()
      return
    }

    // Fetch file details with ACL information before navigation (matches legacy behavior)
    // Legacy state provider calls ApiFiles.getDetails() in the resolve block
    if (workingFile?.id) {
      try {
        // Fetch full file details including ACL level
        const fileDetails = await getDetails(Number(workingFile.id))

        // Check ACL level (matches legacy showFile.stateProvider.js lines 154-160)
        if (fileDetails.acl === FileAclLevel.NONE || fileDetails.acl === FileAclLevel.BROWSE) {
          service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
          return // Don't navigate
        }

        // Check if file is valid
        if (!fileDetails.valid) {
          service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_VALID_MSG'))
          return // Don't navigate
        }

        // Check if file is a placeholder
        if (fileDetails.type === FsiType.PLACEHOLDER) {
          service.toast.warning(translate('GENERATING_VIEWABLE_DOC_PLACEHOLDER_CANNOT_OPEN'))
          return // Don't navigate
        }

        // Check if file is a folder
        if (fileDetails.type === FsiType.FOLDER) {
          service.toast.warning(translate('GENERATING_VIEWABLE_DOC_FOLDER_CANNOT_OPEN'))
          return // Don't navigate
        }

        // User has access and file is valid, close parent modal if callback provided
        if (onNavigate) {
          onNavigate()
        }

        // Navigate to the file viewer
        navigate(`/show-file/${workingFile.id}?action=taskComplete${!isWorkflow ? `&taskId=${task.id}` : ''}`)
      } catch (error: any) {
        // API returned an error (likely 403) - user doesn't have access
        console.error('Error fetching file details:', error)
        if (error?.status === 403) {
          service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
        } else {
          service.toast.warning(translate('ERROR_IN_GENERATING_PROCESS'))
        }
      }
    }
  }

  return (
    <>
      {/* Multiple documents case */}
      {task.filesCount !== undefined && task.filesCount > 1 && (
        <div>
          <div className="d-inline-block">
            <Img
              src={`/assets/images/multiple_docs${isActiveWf ? '_cr' : ''}.svg`}
              alt="multiple docs wf icon"
              className="thumb-image"
            />
          </div>
          <TextLinkButton onClick={handleMultipleDocsClick} style={noPadding ? { padding: '0px 0px' } : undefined}>
            {translate('WORKFLOW_MULTIPLE_DOCS')}
          </TextLinkButton>
        </div>
      )}
      {/* No files or empty file case */}
      {((task?.files && task?.files?.length === 0) || (task?.files?.length === 1 && !workingFile?.name)) && (
        <div className="no-filename">{translate('NOT_AVAILABLE')}</div>
      )}

      {/* Single file case */}
      {task.files && task.files.length === 1 && workingFile?.name && (
        <div className="ellipsis" title={workingFile.name}>
          {!hideIcon && <DocIcon fsi={workingFile as any} inWorkflow={isActiveWf} />}

          {/* Placeholder or no ID case */}
          {(!workingFile.id || workingFile.type === FsiType.PLACEHOLDER) && (
            <span className="whitespaceprewrap" title={workingFile.name}>
              {excerpt(workingFile.name, false, 40)}
            </span>
          )}

          {/* Clickable document link */}
          {workingFile.type !== FsiType.PLACEHOLDER && workingFile.id && (
            <>
              {/* Always use TextLinkButton to validate access before navigation */}
              <TextLinkButton
                onClick={handleDocumentClick}
                className="ellipsis"
                style={noPadding ? { padding: '0px 0px' } : undefined}
              >
                {excerpt(workingFile.name, false, 40)}
              </TextLinkButton>
            </>
          )}

          {/* Document state indicators */}
          {workingFile.id && <DocState fsi={workingFile as any} />}
        </div>
      )}
    </>
  )
}

export default TaskDocLink
